import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/content";
import { scrollToHash } from "@/lib/scroll";

const LINES = [
  { text: "Crafting Edible", accent: false },
  { text: "Masterpieces", accent: true },
  { text: "for Lifelong Memories", accent: false },
];


export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 110]);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden pb-20 pt-32 lg:pb-28 lg:pt-40">
      <div aria-hidden className="pointer-events-none absolute -left-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
      <div aria-hidden className="pointer-events-none absolute -right-24 bottom-0 h-[26rem] w-[26rem] rounded-full bg-[#D4AF37]/5 blur-[100px]" />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-14 left-1/2 -translate-x-1/2 select-none font-display text-[24vw] italic leading-none text-transparent"
        style={{ WebkitTextStroke: "1px rgba(212,175,55,0.07)" }}
      >
        Tarah
      </span>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <div>
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-7 flex items-center gap-4 text-xs uppercase tracking-[0.32em] text-[#D4AF37]"
          >
            <span className="h-px w-12 bg-[#D4AF37]/60" />
            Custom Cakes &amp; Treats · Ireland
          </motion.p>

          <h1 className="font-display text-4xl leading-[1.08] tracking-tight text-[#1B3A6B] sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "italic text-[#D4AF37]" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.15, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-7 max-w-lg text-base leading-relaxed text-[#2F4A78] sm:text-lg"
          >
            Tarah is a design-led cake studio in Ireland, creating bespoke
            birthday, kids, theme and anniversary cakes — alongside refined cookies,
            brownies, cupcakes and bento boxes. Each piece is designed around you and made
            fresh to order, with the care and detail every celebration deserves.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-menu-button"
              onClick={() => scrollToHash("#prices")}
              className="group flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 text-sm font-semibold tracking-wide text-[#1B3A6B] transition-all duration-300 hover:bg-[#8A6D1F]"
            >
              Explore the Menu
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-book-tasting-button"
              onClick={() => scrollToHash("#enquiry")}
              className="rounded-full border border-[#D4AF37]/50 px-8 py-3.5 text-sm font-medium tracking-wide text-[#8A6D1F] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              Send an Enquiry
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <motion.div style={{ y }} className="relative">
            <div className="gold-frame relative overflow-hidden rounded-sm border border-[#D4AF37]/30 bg-[#FFFFFF]/60 p-3 backdrop-blur-xl">
              <img
                data-testid="hero-cake-image"
                src={IMAGES.hero}
                alt="Pink vintage piped ribbon cake by Tarah Cake Design Studio"
                className="aspect-[4/5] w-full rounded-sm object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35, duration: 0.7 }}
              className="gold-glow absolute -bottom-7 -left-4 rounded-sm border border-[#D4AF37]/30 bg-[#FFFFFF]/90 px-6 py-4 backdrop-blur-xl sm:-left-8"
              data-testid="hero-signature-badge"
            >
              <p className="font-label text-[10px] tracking-[0.32em] text-[#9A7B24]">MADE FOR YOU</p>
              <p className="mt-1 font-display text-lg italic text-[#8A6D1F]">Made Fresh to Order</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
